import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError } from '../lib/api'
import { buyerApi, buyerSession, orderStatusLabel, type BuyerOrder } from '../lib/buyer'
import { money, num, shortDate } from '../lib/format'
import { Banner, Empty, Loading, Panel, Pill } from '../ui/kit'
import { BuyerHeader } from './BuyerShell'

export function MyOrders() {
  const [orders, setOrders] = useState<BuyerOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [note, setNote] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null)
  const canOrder = buyerSession.who?.canOrder ?? false

  function load() {
    setLoading(true)
    buyerApi.get<BuyerOrder[]>('/shop/orders')
      .then(setOrders)
      .catch(() => setOrders([]))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  async function cancel(o: BuyerOrder) {
    if (!window.confirm(`Cancel ${o.code}?`)) return
    try {
      await buyerApi.post(`/shop/orders/${o.id}/cancel`, {})
      setNote({ tone: 'ok', text: 'That order was cancelled.' })
      load()
    } catch (e) {
      setNote({ tone: 'crit', text: e instanceof ApiError ? e.message : 'Could not cancel that.' })
    }
  }

  const live = orders.filter((o) =>
    ['draft', 'confirmed', 'allocated', 'picking', 'dispatched'].includes(o.status))
  const past = orders.filter((o) => !live.includes(o))

  return (
    <>
      <BuyerHeader />
      <div className="field-greet">
        <h1>My orders</h1>
        <p>What you have asked for, and where each one has got to.</p>
      </div>

      <div className="field-body">
        {note && (
          <Banner tone={note.tone === 'ok' ? 'info' : 'crit'} title={note.text}>{null}</Banner>
        )}

        {loading && <Panel><Loading rows={4} /></Panel>}

        {!loading && orders.length === 0 && (
          <Empty
            title="You have not ordered anything yet"
            action={canOrder ? <Link className="btn" to="/b">See today's prices</Link> : undefined}
          >
            What you order appears here, so you can see when it is coming.
          </Empty>
        )}

        {live.length > 0 && (
          <Panel title="On the way" meta={`${live.length}`}>
            <div className="panel-body col" style={{ gap: 'var(--sp-6)' }}>
              {live.map((o) => (
                <OrderCard key={o.id} order={o}
                  onCancel={canOrder && ['draft', 'confirmed'].includes(o.status)
                    ? () => void cancel(o) : undefined} />
              ))}
            </div>
          </Panel>
        )}

        {past.length > 0 && (
          <Panel title="Finished" meta={`${past.length}`}>
            <div className="panel-body col" style={{ gap: 'var(--sp-6)' }}>
              {past.map((o) => <OrderCard key={o.id} order={o} />)}
            </div>
          </Panel>
        )}
      </div>
    </>
  )
}

function OrderCard({ order, onCancel }: { order: BuyerOrder; onCancel?: () => void }) {
  const s = orderStatusLabel(order.status)

  return (
    <div style={{
      border: '1px solid var(--line)', borderRadius: 'var(--r-md)',
      padding: 'var(--sp-6) var(--sp-7)', background: 'var(--surface)',
    }}>
      <div className="spread" style={{ alignItems: 'flex-start' }}>
        <div>
          <b style={{ fontSize: 'var(--fs-lg)' }}>{money(order.total)}</b>
          <div className="faint" style={{ fontSize: 'var(--fs-xs)' }}>
            <span className="mono">{order.code}</span> · for {shortDate(order.deliveryDate)}
          </div>
        </div>
        <Pill tone={s.tone} dot={false}>{s.text}</Pill>
      </div>

      <div style={{ marginTop: 'var(--sp-5)' }}>
        {order.lines.map((l, i) => (
          <div className="spread" key={i} style={{ fontSize: 'var(--fs-sm)', padding: '2px 0' }}>
            <span className="muted">
              {l.cropName} {l.gradeCode} · {num(l.orderedKg, 0)} kg
            </span>
            <span className="num">
              {l.unitPrice === null ? '—' : money(Number(l.orderedKg) * Number(l.unitPrice), { currency: false })}
            </span>
          </div>
        ))}
      </div>

      {order.status === 'draft' && (
        <p className="hint" style={{ marginTop: 'var(--sp-5)' }}>
          We have this, but one of our team wants a word before it goes out.
        </p>
      )}

      {order.notes && (
        <p className="hint" style={{ marginTop: 'var(--sp-4)' }}>“{order.notes}”</p>
      )}

      {onCancel && (
        <button className="btn quiet block" style={{ marginTop: 'var(--sp-6)', minHeight: 38 }}
          onClick={onCancel}>
          Cancel this order
        </button>
      )}
    </div>
  )
}
